import { useCallback, useEffect, useMemo,useRef, useState } from 'react';
import { fabric } from 'fabric';


export interface FabricEditor {
  canvas: fabric.Canvas | null;
  addCircle: () => void;
  addRectangle: () => void;
  deleteAll: () => void;
  deleteSelected: () => void;
  clone: () => void;
  toSvg: () => string;
  toJson: () => string;
  fromJson: (json: string) => void;
}

export const useFabricCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [selectedObjects, setSelectedObjects] = useState<fabric.Object[]>([]);


  const editor: FabricEditor = useMemo(() => ({
    canvas,
    addCircle: () => {
      if (canvas) {
        const circle = new fabric.Circle({
          radius: 50,
          fill: 'red',
          left: 100,
          top: 100,
        });
        canvas.add(circle);
      }
    },
    addRectangle: () => {
      if (canvas) {
        const rect = new fabric.Rect({
          left: 100,
          top: 100,
          width: 100,
          height: 100,
          fill: 'blue',
        });
        canvas.add(rect);
      }
    },
    deleteAll: () => {
      if (canvas) {
        canvas.clear();
      }
    },
    deleteSelected: () => {
      if (canvas) {
        const activeObjects = canvas.getActiveObjects();
        activeObjects.forEach((object) => {
          canvas.remove(object);
        });
        canvas.discardActiveObject();
      }
    },
    clone: () => {
      if (canvas) {
        const activeObject = canvas.getActiveObject();
        if (activeObject) {
          activeObject.clone((cloned: fabric.Object) => {
            cloned.set({
              left: (cloned.left || 0) + 10,
              top: (cloned.top || 0) + 10,
            });
            canvas.add(cloned);
          });
        }
      }
    },
    toSvg: () => {
      return canvas?.toSVG() || '';
    },
    toJson: () => {
      return JSON.stringify(canvas?.toJSON() || {});
    },
    fromJson: (json: string) => {
      if (canvas) {
        canvas.loadFromJSON(json, () => {
          canvas.renderAll();
        });
      }
    },
  }), [canvas]);

  useEffect(() => {
    if (canvasRef.current && !canvas) {
      const fabricCanvas = new fabric.Canvas(canvasRef.current);
      
      fabricCanvas.on('selection:created', (e) => {
        setSelectedObjects(e.selected || []);
      });
      
      fabricCanvas.on('selection:updated', (e) => {
        setSelectedObjects(e.selected || []);
      });
      
      fabricCanvas.on('selection:cleared', () => {
        setSelectedObjects([]);
      });

      setCanvas(fabricCanvas);
    }

    return () => {
      if (canvas) {
        try {
          canvas.dispose();
        } catch (e) {
          // Ignore disposal errors during HMR
        }
      }
    };
  }, []);

  return {
    canvasRef,
    editor,
    selectedObjects,
  };
};